import { graphql } from '@apollo/client/react/hoc';
import { flowRight as compose } from 'lodash';
import * as React from 'react';
import cx from 'classnames';
import { connect } from 'react-redux';

import Glyph from 'components/Glyph';
import IndividualChatStarter from 'components/IndividualChatStarter';
import Link from 'components/Link';
import List from 'components/List';
import ListItem from 'components/ListItem';
import MediaObject from 'components/MediaObject';
import ModalTrigger from 'components/ModalTrigger';
import PhoneCallNoteModal from 'phoneCalls/components/PhoneCallNoteModal';
import Text from 'components/Text';
import TextWithGlyph from 'components/TextWithGlyph';
import Timer from 'components/Timer';
import User from 'entities/User';
import UserAvatar from 'users/components/UserAvatar';
import PHONE_CALL_QUERY from '../queries/PhoneCallFloatingBox.gql';
import {
  PhoneCallFloatingBox as Data,
  PhoneCallFloatingBoxVariables as Variables,
} from '../queries/__generated__/PhoneCallFloatingBox';

import './styles/PhoneCallFloatingBox.scss';

// Call statuses: https://support.twilio.com/hc/en-us/articles/223132547-What-are-the-Possible-Call-Statuses-and-What-do-They-Mean-
const STATUS_TO_HUMAN_STATUS = {
  in_progress: 'Call in progress',
  outgoing: 'Outgoing call',
  incoming: 'Incoming call',
  missed: 'Missed call',
  failed: 'Outgoing call',
  canceled: 'Canceled call',
  unknown: 'Call status unknown',
  no_answer: 'Missed call',
  completed: 'Completed',
  caller_unreachable: 'Missed call',
  busy: 'Missed call',
  ringing: 'Ringing',
  initiated: 'Calling',
  queued: 'outgoing call',
};

type Theme = 'in_progress' | 'terminated';

export type Props = {
  // the phone call uuid.
  uuid?: string;
  // a callback to close the box.
  remove: () => Promise<any>;
};

export type InnerProps = {
  phoneCall?: Data['phoneCall'];
  // the last time we received a push event for the phone call
  lastPhoneCallPushEvent?: Date | null;
  // apollo - refetch the phone call
  refetchPhoneCall?: Function;
};

export class PhoneCallFloatingBox extends React.Component<Props & InnerProps> {
  static WIDTH = 400;
  static HEIGHT = 123;
  static POSITION = 'top-right';
  static SUPPRESS_TEXT_SELECTION = true;

  componentWillReceiveProps(nextProps: Props & InnerProps) {
    if (nextProps.lastPhoneCallPushEvent !== this.props.lastPhoneCallPushEvent) {
      this.props.refetchPhoneCall?.();
    }
  }

  render() {
    if (!this.props.phoneCall) {
      return null;
    }

    const { phoneCall } = this.props;
    const theme: Theme =
      phoneCall.status_localization_key === 'in_progress' ? 'in_progress' : 'terminated';

    const classnames = cx('PhoneCallFloatingBox-call', {
      'PhoneCallFloatingBox-call--mascot': theme === 'in_progress',
      'PhoneCallFloatingBox-call--light': theme === 'terminated',
    });

    const canChat = phoneCall.otherUser?.chat_eligibility_state === 'eligible';

    return (
      <div className="PhoneCallFloatingBox">
        <div className={classnames}>
          <div className="PhoneCallFloatingBox-call-info">
            <div>
              <MediaObject center gutter="small">
                {/* @ts-expect-error */}
                <UserAvatar user={phoneCall.otherUser} size="medium" className="Avatar--grey" />
                <div>
                  <Text size="medium" element="div">
                    {phoneCall.otherUser?.name}
                  </Text>
                  <Text weight="normal" element="div">
                    {/* @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message */}
                    {STATUS_TO_HUMAN_STATUS[phoneCall.status]}
                  </Text>
                </div>
              </MediaObject>
            </div>
          </div>
          <div className="PhoneCallFloatingBox-call-duration">
            {theme === 'in_progress' ? (
              <Text size="xlarge">
                <Timer start={new Date(phoneCall.started_at || '')} />
              </Text>
            ) : (
              // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
              <Glyph name="close" size="medium" onClick={this.props.remove} />
            )}
          </div>
        </div>
        <div className="PhoneCallFloatingBox-actions">
          {/* @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call. */}
          <List size="medium">
            <ListItem>
              <ModalTrigger modal={<PhoneCallNoteModal uuid={this.props.uuid} />}>
                <Link>
                  <TextWithGlyph glyph="note" glyphSize="large">
                    {String(phoneCall.note).length === 0 && phoneCall.reasons?.length === 0
                      ? 'Add call notes'
                      : 'Edit call notes'}
                  </TextWithGlyph>
                </Link>
              </ModalTrigger>
            </ListItem>
            {canChat && theme === 'terminated' && (
              <ListItem>
                <IndividualChatStarter
                  user={User.fromJSON(phoneCall.otherUser)}
                  onTransitionStart={this.props.remove}
                >
                  <TextWithGlyph glyph="chat-add-bubble" glyphSize="large">
                    Send Message
                  </TextWithGlyph>
                </IndividualChatStarter>
              </ListItem>
            )}
          </List>
        </div>
      </div>
    );
  }
}

const withPhoneCall = graphql<Props, Data, Variables, InnerProps>(PHONE_CALL_QUERY, {
  // TODO check the default value
  options: ({ uuid }) => ({ variables: { uuid: uuid || '' } }),
  props: ({ data }) => ({
    phoneCall: data?.phoneCall || undefined,
    refetchPhoneCall: data?.refetch,
  }),
});

function mapStateToProps(state: any, props: Props) {
  const phoneCall = state.phoneCalls.get(props.uuid);

  return {
    lastPhoneCallPushEvent: phoneCall ? phoneCall.received_at : undefined,
  };
}

export default (compose as any)(
  connect(mapStateToProps),
  withPhoneCall
)(PhoneCallFloatingBox) as React.ComponentType<Props>;
